import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "./Avatar";

// Full-screen "Mochi waited for you" intro animation that plays once when
// the user returns after being away ≥ 1 day. Auto-dismisses after ~2.4s
// (or earlier if the user taps). Pure cosmetic — does not block any data.
//
//   <MochiWaitedOverlay open={daysAway >= 1} daysAway={2} onDismiss={...} />
export default function MochiWaitedOverlay({ open, daysAway = 1, onDismiss }) {
    React.useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => onDismiss?.(), 2400);
        return () => clearTimeout(t);
    }, [open, onDismiss]);

    const caption =
        daysAway >= 3
            ? "Mochi reorganized your pile."
            : daysAway === 2
            ? "Mochi missed you."
            : "Mochi waited for you.";

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeInOut" } }}
                    transition={{ duration: 0.35 }}
                    onClick={() => onDismiss?.()}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-cream"
                    data-testid="mochi-waited-overlay"
                >
                    {/* Soft warm glow */}
                    <motion.div
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1.2, opacity: 0.55 }}
                        transition={{ duration: 1.4, ease: "easeOut" }}
                        className="absolute w-[320px] h-[320px] rounded-full blur-3xl"
                        style={{ background: "radial-gradient(circle, rgba(255,210,63,0.45), transparent 70%)" }}
                    />

                    {/* Sparkles drifting up */}
                    {[...Array(6)].map((_, i) => (
                        <motion.span
                            key={`waited-sparkle-${i}`}
                            initial={{ opacity: 0, y: 30, scale: 0.4 }}
                            animate={{ opacity: [0, 1, 0], y: -120, scale: [0.4, 1, 0.6] }}
                            transition={{ duration: 2.2, delay: 0.2 + i * 0.18, ease: "easeOut" }}
                            className="absolute text-mustard text-xl"
                            style={{ left: `${30 + (i % 3) * 20}%`, top: `${55 + (i % 2) * 6}%` }}
                            aria-hidden
                        >
                            ✦
                        </motion.span>
                    ))}

                    <div className="relative flex flex-col items-center gap-4">
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: [10, -4, 0] }}
                            transition={{ duration: 0.9, ease: [0.22, 1.2, 0.36, 1] }}
                        >
                            <Avatar mood="happy" size={180} />
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.4 }}
                            className="font-display text-warm-900 text-2xl tracking-tight text-center"
                            data-testid="mochi-waited-caption"
                        >
                            {caption}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.7 }}
                            transition={{ delay: 0.9, duration: 0.4 }}
                            className="font-body text-warm-500 text-xs uppercase tracking-widest"
                        >
                            {daysAway >= 2 ? `${daysAway} days · welcome back` : "welcome back"}
                        </motion.p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
